import { jsx as _jsx } from "react/jsx-runtime";
import { Component, type ReactNode } from 'react';
import { validateLegacyTemplate } from '../lib/legacyExpressions.mjs';
import { Grid, Span } from './Grid';
import HeaderBlock from './blocks/HeaderBlock';
import KeyValueBlock from './blocks/KeyValueBlock';
import TableBlock from './blocks/TableBlock';
import TextBlock from './blocks/TextBlock';
import TotalsBlock from './blocks/TotalsBlock';
import SpacerBlock from './blocks/SpacerBlock';
import RepeatBlock from './blocks/RepeatBlock';
import GroupBlock from './blocks/GroupBlock';   // NEW
import ImageBlock from './blocks/ImageBlock';   // NEW

function ReportExpressionFailure() {
  return <div role="alert" data-report-error="UNSUPPORTED_LEGACY_EXPRESSION">
    This report could not be rendered because it contains an unsupported expression or binding.
  </div>;
}

class ReportExpressionBoundary extends Component<
  { template: unknown; data: unknown; children: ReactNode },
  { failed: boolean; template: unknown; data: unknown }
> {
  state = { failed: false, template: this.props.template, data: this.props.data };

  static getDerivedStateFromProps(props: { template: unknown; data: unknown }, state: { template: unknown; data: unknown }) {
    if (props.template !== state.template || props.data !== state.data) {
      return { failed: false, template: props.template, data: props.data };
    }
    return null;
  }

  static getDerivedStateFromError() { return { failed: true }; }

  render() { return this.state.failed ? <ReportExpressionFailure /> : this.props.children; }
}

export function ReportRenderer({ template, data, onSortChange }: any) {
  try {
    validateLegacyTemplate(template);
  } catch {
    return <ReportExpressionFailure />;
  }
  return <ReportExpressionBoundary template={template} data={data}>
    <LegacyReport template={template} data={data} onSortChange={onSortChange} />
  </ReportExpressionBoundary>;
}

function LegacyReport({ template, data, onSortChange }: any) {
  const t = template.theme.tokens;
  const isLandscape = template?.doc?.landscape === true;
  return (
    _jsx("div", {
      className: `report-root${isLandscape ? ' report-root--landscape' : ''}`,
      "data-template-id": template.id, // enable per-template CSS scoping
      "data-report-root": true,
      style: {
        '--colorPrimary': t.colorPrimary,
        '--colorText': t.colorText,
        '--colorMuted': t.colorMuted,
        '--bgCard': t.bgCard,
        '--radius': `${t.borderRadius}px`,
        fontFamily: t.fontFamily
      } as React.CSSProperties,
      children: _jsx(Grid, {
        cols: template.layout.columns,
        gutter: template.layout.gutter,
        children: template.layout.sections.map((sec: any) => (
          _jsx(Span, {
            from: sec.span.from,
            to: sec.span.to,
            children: (
              <div className={sec.className || ''}>
                {sec.blocks.map((b: any, i: number) => _jsx(BlockRenderer, { block: b, data, template, onSortChange }, i))}
              </div>
            )
          }, sec.id)
        ))
      })
    })
  );
}

export function BlockRenderer({ block, data, template, onSortChange }: any) {
  switch (block.type) {
    case 'header':  return _jsx(HeaderBlock,  { block, data });
    case 'keyValue':return _jsx(KeyValueBlock,{ block, data });
    case 'table':   return _jsx(TableBlock,   { block, data, onSortChange });
    case 'text': return _jsx(TextBlock, { block, data, template });
    case 'totals':  return _jsx(TotalsBlock,  { block, data });
    case 'spacer':  return _jsx(SpacerBlock,  { block });
    case 'repeat':  return _jsx(RepeatBlock, { block, data, template });
    case 'group':   return _jsx(GroupBlock,  { block, data, template });
    case 'image':   return _jsx(ImageBlock,   { block, data });
    default:        return null;
  }
}